import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled as muiStyled,
} from '@mui/material';
import { MouseEvent, useRef, useState } from 'react';

import styled from '@emotion/styled';
import { Badge } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthentication } from '../authentication/context/useAuthentication';
import useUser, { isUserSubscribed } from '../authentication/context/useUser';
import PasswordChangeModal from '../authentication/PasswordChangeModal';
import ProfileChangeModal from '../authentication/ProfileChangeModal';
import { ReactComponent as AccountIcon } from '../ui/icons/account.svg';
import { ReactComponent as PasswordIcon } from '../ui/icons/change-password.svg';
import { ReactComponent as CrownIcon } from '../ui/icons/couronne.svg';
import { ReactComponent as ExitIcon } from '../ui/icons/exit.svg';

const LogoWrapper = styled.img`
  max-width: 80px;
  max-height: 50px;
`;

const contrastColor = (backgroundColor: string) => {
  try {
    return ['var(--textColor)', 'white'][
      ~~(
        [1, 3, 5]
          .map((p) => parseInt(backgroundColor.substr(p, 2), 16))
          .reduce((r, v, i) => [0.299, 0.587, 0.114][i] * v + r, 0) < 128
      )
    ];
  } catch (e) {
    return 'var(--textColor)';
  }
};

const stringToColor = (name: string, saturation = 100, lightness = 75) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  return {
    bgcolor: `hsl(${hash % 360}, ${saturation}%, ${lightness}%)`,
    color: contrastColor(`${hash % 360}`),
  };
};

const stringAvatar = (name: string) => {
  return {
    sx: {
      cursor: 'pointer',
      border: '2px solid var(--textColor)',
      ...stringToColor(name),
    },
    children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
  };
};

const Title = styled.p`
  font-size: 1rem;
  font-weight: 500;
  text-align: left;
  span {
    font-weight: bold;
  }
`;

export const SubscribeMenuItem = muiStyled(MenuItem)(({ theme }) => ({
  backgroundColor: 'var(--purple)',
  margin: '6px 8px',
  padding: '4px 8px',
  borderRadius: '0',
  fontWeight: 600,
  color: 'white',
  '&:hover': {
    backgroundColor: 'var(--lightpurple)',
  },
}));

const CrownContainer = styled.p`
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CrownIconComponent = () => {
  return (
    <CrownContainer>
      <CrownIcon style={{ width: '16px', height: '16px', color: 'white' }} />
    </CrownContainer>
  );
};

const UserMenu = () => {
  const { t } = useTranslation(['authentication', 'common']);
  const { logout } = useAuthentication();
  const { user } = useUser();
  const navigate = useNavigate();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  if (!user) return null;

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePasswordChangeClick = () => {
    handleClose();
    setPasswordModalOpen(true);
  };

  const handleProfileChangeClick = () => {
    handleClose();
    setProfileModalOpen(true);
  };

  const handleSubscribe = async () => {
    navigate('/checkout');
  };

  return (
    <>
      <PasswordChangeModal
        open={passwordModalOpen}
        handleClose={() => setPasswordModalOpen(false)}
      />
      {profileModalOpen && (
        <ProfileChangeModal
          open={profileModalOpen}
          handleClose={() => setProfileModalOpen(false)}
        />
      )}
      <Badge
        invisible={!isUserSubscribed(user)}
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        badgeContent={
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '50%',
              padding: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
            }}
          >
            <CrownIcon
              style={{ width: '16px', height: '16px', fill: 'var(--purple)' }}
            />
          </div>
        }
      >
        <Avatar
          {...stringAvatar(`${user?.firstName[0]} ${user?.lastName[0]}`)}
          onClick={handleClick}
          ref={avatarRef}
          sx={{
            backgroundColor: 'var(--color-primary)',
            cursor: 'pointer',
            fontFamily: 'Ibrand',
            fontWeight: '500',
          }}
        />
      </Badge>

      <Menu
        id="user-menu"
        open={open}
        anchorEl={avatarRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box
          sx={{ padding: '8px 16px', display: 'flex', justifyContent: 'left' }}
        >
          <LogoWrapper id="logoWrapper" src="/logo vert.png" alt="logo" />
        </Box>

        <Divider sx={{ margin: '8px 0' }} />

        <Box sx={{ padding: '8px 16px' }}>
          <Title>
            {t('connected-as')}{' '}
            <span>
              {user?.firstName} {user?.lastName}
            </span>
          </Title>
        </Box>

        <Divider sx={{ margin: '8px 0' }} />

        {!isUserSubscribed(user) ? (
          <SubscribeMenuItem onClick={handleSubscribe}>
            <ListItemIcon>
              <CrownIconComponent />
            </ListItemIcon>
            <ListItemText>
              {t('common:subscription.userMenu.subscriptionButton')}
            </ListItemText>
          </SubscribeMenuItem>
        ) : (
          <>
            <SubscribeMenuItem
              sx={{ cursor: 'default', pointerEvents: 'none' }}
            >
              <ListItemIcon>
                <CrownIconComponent />
              </ListItemIcon>
              <ListItemText>
                {t('common:subscription.userMenu.subscriptionInfo', {
                  expirationDate: new Date(
                    user.subscriptionExpirationAt,
                  ).toLocaleDateString(),
                })}
              </ListItemText>
            </SubscribeMenuItem>
            <Divider sx={{ margin: '8px 0' }} />
          </>
        )}

        <MenuItem onClick={handleProfileChangeClick}>
          <ListItemIcon>
            <AccountIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('edit-profile')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePasswordChangeClick}>
          <ListItemIcon>
            <PasswordIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('change-password')}</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={logout}>
          <ListItemIcon>
            <ExitIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
